import {FC, useState} from "react";
import {MedicationsTableProps, times} from "./types";
import {useDispatch, useSelector} from "react-redux";
import {getCurrentSchedules} from "../../../../../../../redux/selectors/patientsSelector";
import {IMedication, ToastType} from "../../../../../../../utils/interfaces";
import {formatMedicationName, getDates} from "./utils";
import classes from './MedicationsTable.module.scss';
import {Button, Menu, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography} from "@mui/material";
import {format, isBefore, isToday, parse} from "date-fns";
import {SET_TOAST} from "../../../../../../../redux/actions";
import {ModalWithContent} from "../../../../../../common/ModalWithContent/ModalWithContent";

type InfoItemProps = {
  name: string;
  value: string;
}

const InfoItem: FC<InfoItemProps> = ({name, value}) => {
  return (
    <div className={classes.medicationInfoDialogItem}>
      <Typography color="GrayText" variant="overline">{name}: </Typography>
      <Typography variant="overline">{value}</Typography>
    </div>
  )
}

type MenuDataType = {
  selectedMedication: null | IMedication;
  dialog: {
    open: boolean;
  };
  menu: {
    anchorEl: null | HTMLDivElement;
  };
}


export const MedicationsTable: FC<MedicationsTableProps> = ({
  onEditMedication,
  onRemoveMedication,
  period,
  onAddMedicationForSpecificTimeslot,
  nameMap,
  readOnly = false,
}) => {
  const patientMedications: Record<string, IMedication[]> = useSelector(getCurrentSchedules);
  const datesArray = getDates(period);
  const dispatch = useDispatch();

  const [{selectedMedication, dialog: {open}, menu: {anchorEl}}, setMenuData] = useState<MenuDataType>({
    selectedMedication: null,
    dialog: {
      open: false,
    },
    menu: {
      anchorEl: null,
    },
  });

  const handleClick = (data: IMedication, event: React.MouseEvent<HTMLDivElement>) => {
    if (readOnly) return;

    setMenuData(prev => ({
      ...prev,
      selectedMedication: data,
      menu: {
        anchorEl: event.currentTarget
      }
    }));
  };

  const handleClose = () => {
    setMenuData({
      selectedMedication: null,
      menu: {
        anchorEl: null,
      },
      dialog: {
        open: false,
      }
    });
  };

  const handleEdit = () => {
    if (!readOnly && selectedMedication) {
      const startDate = parse(selectedMedication.startDate, 'yyyy-MM-dd', new Date());
      const endDate = parse(selectedMedication.endDate, 'yyyy-MM-dd', new Date());

      if (isBefore(startDate, new Date()) && isBefore(endDate, new Date()) && !isToday(endDate)) {
        dispatch({
          type: SET_TOAST,
          payload: {
            open: true,
            message: "You can't change finished medications",
            type: ToastType.ERROR,
          },
        })
        return;
      }

      handleClose();
      setTimeout(() => onEditMedication(selectedMedication), 100);
    }
  }

  const handleDelete = () => {
    if (!readOnly && selectedMedication) {
      handleClose();
      setTimeout(() => onRemoveMedication(selectedMedication), 100);
    }
  }

  const handleInfo = () => {
    if (selectedMedication) {
      setMenuData(prev => ({
        ...prev,
        menu: {
          anchorEl: null,
        },
      }));

      setTimeout(() => {
        setMenuData(prev => ({
          ...prev,
          dialog: {
            open: true,
          }
        }))
      }, 100);
    }
  }

  const handleClickOnEmptyCell = (isEmpty: boolean) => {
    if (isEmpty) {
      onAddMedicationForSpecificTimeslot();
    }
  }

  return (
    <>
      <TableContainer className={classes.tableContainer} component={Paper}>
        <Table stickyHeader aria-label="simple table">
          <TableHead>
            <TableRow classes={{
              root: classes.tableHeader
            }}>
              <TableCell className={classes.stickyColumn}></TableCell>
              {datesArray.map(date => (
                <TableCell className={classes.tableCell} align="center" key={date}>
                  <Typography variant="overline">{format(parse(date, 'yyyy-MM-dd', new Date()), 'dd.MM.yy')}</Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {times.map(time => {
              const isEmptyRow = datesArray.every(date => {
                const medicationsForCurrentDate = patientMedications[date] ?? [];

                return !medicationsForCurrentDate.some(medication => medication.times.some(medicationTime => {
                  const splittedMedicationTime = medicationTime.split(':');

                  return splittedMedicationTime[0] === time.split(':')[0];
                }));
              });

              return (
                <TableRow key={time} className={isEmptyRow ? classes.emptyRow : undefined}>
                  <TableCell
                    size="small"
                    className={`${classes.stickyColumnTimes} ${isEmptyRow ? classes.emptyRowTimeCell : ''}`}
                    component="th"
                    scope="row"
                  >
                    <Typography variant="overline" className={isEmptyRow ? classes.emptyRowTime : undefined}>{time}</Typography>
                  </TableCell>
                  {datesArray.map(date => {
                    const medicationsForCurrentDate = patientMedications[date] ?? [];
                    const medicationForCurrentTime = medicationsForCurrentDate.filter(medication => medication.times.some(medicationTime => {
                      const splittedMedicationTime = medicationTime.split(':');

                      return splittedMedicationTime[0] === time.split(':')[0];
                    }));

                    return (
                      <TableCell onClick={() => !readOnly && handleClickOnEmptyCell(!medicationForCurrentTime.length)} key={date} className={medicationForCurrentTime.length ? classes.tableCell : classes.emptyTableCell}>
                        <div className={classes.medicationContainer}>
                          {medicationForCurrentTime.sort((medicationA, medicationB) => medicationA.name.localeCompare(medicationB.name)).map(medication => (
                            <Tooltip title={medication.name}>
                              <div onClick={(event) => handleClick(medication, event)} key={medication.id} className={classes.medication} style={{backgroundColor: medication.colorHex}}>
                                <Stack direction="column">
                                  <Typography noWrap variant="overline">
                                    {nameMap[medication.name] || formatMedicationName(medication.name)} {medication.dosageValue} {medication.dosageUnit}
                                  </Typography>
                                </Stack>
                              </div>
                            </Tooltip>
                          ))}
                        </div>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ModalWithContent
        title={selectedMedication?.name ?? ''}
        open={open}
        onClose={handleClose}
        actions={
          <div style={{display: 'flex', flexDirection: 'row', gap: 12}}>
            <Button
              className={classes.action}
              onClick={handleEdit}
              sx={{flex: 1}}
              variant="contained">
              Edit
            </Button>
            <Button
              className={classes.action}
              sx={{flex: 1}}
              onClick={handleDelete}
              color="error"
              variant="outlined">
              Remove
            </Button>
          </div>
        }>
        <Stack direction="column" spacing={1}>
          <InfoItem name="Type" value={selectedMedication?.type ?? ''} />
          <InfoItem name="Dosage" value={`${selectedMedication?.dosageValue} ${selectedMedication?.dosageUnit}`} />
          <InfoItem name="Frequency" value={selectedMedication?.times.join(', ') ?? ''} />
          <InfoItem name="Duration" value={`${selectedMedication?.startDate} - ${selectedMedication?.endDate}`} />
        </Stack>
      </ModalWithContent>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleInfo}>Info</MenuItem>
        <MenuItem sx={{color: 'red'}} onClick={handleDelete}>Remove</MenuItem>
      </Menu>
    </>
  );
}
